import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { authenticatedHttp } from '@/lib/services/authenticated-http';
import { useCreateFileMutation } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

interface UseUploadFileOptions {
  entityType: 'account' | 'contact' | 'deal';
  entityId: string;
  onSuccess?: () => void;
}

export function useUploadFile({ entityType, entityId, onSuccess }: UseUploadFileOptions) {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [createFileMutation] = useCreateFileMutation();

  const uploadFile = useCallback(async (file: File) => {
    if (!workspaceId) {
      toast.error(t('mutations.noWorkspaceSelected'));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('mutations.fileTooLarge'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Upload binary to S3 via REST endpoint
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedHttp.post('/files/upload-file', formData, {
        headers: { 'Content-Type': undefined },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(pct);
          }
        },
      });

      const { fileUrl, filename, mimeType, size } = response.data;

      // Step 2: Create file record via GraphQL mutation
      const entityIds: Record<string, string[]> = {
        accountIds: entityType === 'account' ? [entityId] : [],
        contactIds: entityType === 'contact' ? [entityId] : [],
        dealIds: entityType === 'deal' ? [entityId] : [],
      };

      await createFileMutation({
        variables: {
          workspaceId,
          filename,
          mimeType,
          size,
          fileUrl,
          ...entityIds,
        },
      });

      toast.success(t('mutations.fileUploaded', { filename }));
      onSuccess?.();
      // Backend indexes files to OpenSearch asynchronously after MongoDB write;
      // the initial refetch may return stale data, so retry after a short delay
      setTimeout(() => onSuccess?.(), 2000);
    } catch (err) {
      console.error('File upload failed:', err);
      toast.error(t('mutations.fileUploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [workspaceId, entityType, entityId, createFileMutation, onSuccess, t]);

  return { uploadFile, uploading, uploadProgress };
}
